import type { OrderMapOffer } from '../model/order-schema';
import {
  createOfferCatalog,
  tallyOfferStats,
  toLatLong,
  type OfferReason,
  type OfferRecord,
  type OfferTone,
  type OffersView,
} from './offers';

/**
 * The "new" offers source: the Delivery service's `GET /api/v1/admin/orders/{id}/offers`
 * `list` (`reason` = the backend enum, `offer_decision` slugs, `offer_count`).
 */

const reason = (id: string, number: number, tone: OfferTone): OfferReason => ({
  id,
  number,
  tone,
  labelKey: `reason.${id}`,
});

/**
 * Why a courier was not offered / did not take an offer — the backend's own
 * reason enum (`offer.reason`, 1–14), which supersedes cpanel's older table
 * (its numbers meant different things: there `2` was "volume", here `2` is
 * "wrong equipment"). Tones carry over from cpanel where the reason did.
 */
export const OFFER_REASONS: readonly OfferReason[] = [
  reason('ReasonNotWorkingInt', 1, 'neutral'),
  reason('ReasonWrongEquipmentInt', 2, 'pink'),
  reason('ReasonBalanceInt', 3, 'sky'),
  reason('ReasonHasActiveOrderInt', 4, 'blue'),
  reason('ReasonMaxOrderCountReached', 5, 'amber'),
  reason('ReasonVolumeInt', 6, 'orange'),
  reason('ReasonMultiorderInvalidRoute', 7, 'red-soft'),
  reason('ReasonOrderLateMinutes', 8, 'orange-soft'),
  reason('ReasonPointDifferExceeded', 9, 'violet-soft'),
  reason('ReasonCourierExclusiveOrderInProgress', 10, 'blue-soft'),
  reason('ReasonExclusiveNewOrderNotAtFinalDest', 11, 'violet'),
  reason('ReasonExclusiveNewOrderRequiresSingleActive', 12, 'orange-deep'),
  reason('ReasonMapDistanceUnavailable', 13, 'teal'),
  reason('ReasonMultiorderNewOrderNotInEndRadius', 14, 'amber-soft'),
];

export const NEW_OFFER_CATALOG = createOfferCatalog('new', OFFER_REASONS);

const REJECT_DECISION = /^reject_reason_(\d+)$/;

/**
 * The reason number of an offer: the explicit `reason`, else the one encoded in
 * a `reject_reason_N` decision. Null when the courier simply had no rejection
 * reason (accepted, or still pending).
 */
export function offerReasonNumber(
  offer: Pick<OrderMapOffer, 'reason' | 'offer_decision'>,
): number | null {
  if (offer.reason) return offer.reason;
  const match = REJECT_DECISION.exec(offer.offer_decision ?? '');
  return match ? Number(match[1]) : null;
}

/** Whether the courier took the order (`offer_decision` starts with "accept"). */
export function isOfferAccepted(offer: Pick<OrderMapOffer, 'offer_decision'>): boolean {
  return /^accept/i.test(offer.offer_decision ?? '');
}

/**
 * The dispatch stage that decided an offer, from the base64 JSON
 * `decision_detail` (`{"stage":"basic_check"}`). Null when absent or unreadable.
 */
export function decodeDecisionStage(detail: string | null | undefined): string | null {
  if (!detail) return null;
  try {
    const parsed: unknown = JSON.parse(atob(detail));
    const stage =
      typeof parsed === 'object' && parsed !== null && 'stage' in parsed
        ? parsed.stage
        : null;
    return typeof stage === 'string' && stage ? stage : null;
  } catch {
    return null;
  }
}

export function newOfferRecords(offers: readonly OrderMapOffer[]): OfferRecord[] {
  return offers.map((raw) => ({
    mode: 'new',
    raw,
    id: raw.id ?? null,
    courierId: raw.courier_id ?? null,
    coords: toLatLong(raw.courier_location),
    roadMeters: raw.dead_head_distance ?? null,
    facts: {
      reason: offerReasonNumber(raw),
      accepted: isOfferAccepted(raw),
      // No screen impression is recorded when `offer_count` is 0.
      reached: (raw.offer_count ?? 0) > 0,
    },
    sortAt: raw.created_at ?? null,
  }));
}

export function newOffersView(offers: readonly OrderMapOffer[]): OffersView {
  const records = newOfferRecords(offers);
  return { mode: 'new', records, stats: tallyOfferStats(records, NEW_OFFER_CATALOG) };
}
